import { useCallback, useEffect, useState } from 'react';
import { orderBy, uniqBy } from 'lodash-es';
import { gql } from '@soundxyz/gql-string';
import { useAuth } from '@context/auth/base';
import type { NftCursorConnectionArgs, UserNfTsForReleaseQuery } from '@graphql/reactQuery';
import { useQuery } from '@graphql/reactQuery';
import { UserNfTsForReleaseDocument } from '@graphql/reactQuery';

/*#__PURE__*/ gql(/* GraphQL */ `
  query userNFTsForRelease(
    $id: UUID!
    $filter: UserNftsConnectionFilters
    $pagination: NftCursorConnectionArgs!
  ) {
    user(id: $id) {
      id
      nftsPaginated(filter: $filter, pagination: $pagination) {
        pageInfo {
          hasNextPage
          endCursor
        }
        edges {
          node {
            id
            songSlot
            comment {
              id
              message
            }
            owner {
              id
              publicAddress
            }
            serialNumber
          }
          cursor
        }
      }
    }
  }
`);

export type UserNft = NonNullable<
  UserNfTsForReleaseQuery['user']
>['nftsPaginated']['edges'][number]['node'];

export function useMultipleNftsForRelease({ releaseId }: { releaseId: string }) {
  const { authUser } = useAuth();

  const [nftsOwnedByUser, setNftsOwnedByUser] = useState<UserNft[]>([]);
  const [pagination, setPagination] = useState<NftCursorConnectionArgs>({
    first: 50,
    sort: {
      serialNumber: 'ASC',
    },
    after: null,
  });

  const addOwners = useCallback(
    (nfts: UserNft[]) => {
      setNftsOwnedByUser(current =>
        uniqBy(
          orderBy([...current, ...nfts], n => n.serialNumber, 'asc'),
          n => n.id,
        ),
      );
    },
    [setNftsOwnedByUser],
  );

  useEffect(() => {
    setNftsOwnedByUser([]);
  }, [releaseId]);

  const { data, isLoading: loading } = useQuery(UserNfTsForReleaseDocument, {
    variables: !!authUser && {
      id: authUser.id,
      filter: {
        releases: [releaseId],
      },
      pagination,
    },
    staleTime: 0,
  });

  const pageInfo = data?.data.user?.nftsPaginated.pageInfo;

  useEffect(() => {
    addOwners(data?.data.user?.nftsPaginated.edges.map(edge => edge.node) || []);
  }, [data?.data.user?.nftsPaginated, addOwners]);

  const loadMore = useCallback(() => {
    if (!pageInfo?.hasNextPage) return;

    setPagination(current => ({
      ...current,
      first: 10,
      after: pageInfo.endCursor,
    }));
  }, [pageInfo?.hasNextPage, pageInfo?.endCursor]);

  return {
    nftsOwnedByUser,
    loadMore,
    loading,
  };
}
